import React from 'react';
import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentSearchSnippetProps {
  /**
   * Content object that may carry a `searchSnippet` (or snake_case
   * `search_snippet`) populated by the search backend when the query matched
   * archive manifest entry paths rather than title/description/tags.
   */
  content: { searchSnippet?: string | null; search_snippet?: string | null };
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * Renders a "Contains: <path>" snippet under a search result so users can see
 * *why* an archive appeared in results (the query matched a file inside the
 * archive, not the archive's own title or description). Renders nothing when
 * the snippet is absent — only present on search responses for archive
 * content whose manifest matched the query.
 */
export const ContentSearchSnippet: React.FC<ContentSearchSnippetProps> = ({ content, sx }) => {
  const snippet = content.searchSnippet || content.search_snippet;
  if (!snippet) return null;
  return (
    <Typography
      variant="caption"
      sx={[
        {
          display: 'block',
          color: (theme) => theme.palette.text.secondary,
          fontStyle: 'italic',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {snippet}
    </Typography>
  );
};

export default ContentSearchSnippet;
